import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Images from '../../assests/images/Images';
import CtaButton from '../../components/common/CtaButton';

/* Directional Scroll & Page Load Reveal Hook */
const useScrollReveal = () => {
  useEffect(() => {
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = document.querySelectorAll(
      '.sipl-reveal, .sipl-reveal-ltr, .sipl-reveal-rtl, .sipl-reveal-ttb, .sipl-reveal-btt'
    );

    if (isReduced) {
      elements.forEach((el) => el.classList.add('sipl-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('sipl-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    const checkScrollPosition = () => {
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= (window.innerHeight || document.documentElement.clientHeight) - 20) {
          el.classList.add('sipl-revealed');
        }
      });
    };

    checkScrollPosition();
    window.addEventListener('scroll', checkScrollPosition, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
      observer.disconnect();
    };
  }, []);
};

const DYNAMIC_MESSAGES = [
  { text: 'Tailored ERP, CRM, and HRM business software systems.', color: '#4caf50' },
  { text: 'Centralize inventory, sales pipelines, and payroll.', color: '#1a2f6e' },
  { text: 'Automate manual spreadsheets into 1-click workflows.', color: '#0d9488' },
  { text: 'Role-based access security and real-time analytics.', color: '#f97316' },
];

const MARQUEE_ITEMS = [
  { label: 'Custom Enterprise ERP', color: '#4caf50' },
  { label: 'Automated Payroll & HRM', color: '#1a2f6e' },
  { label: 'CRM Sales Pipelines', color: '#0d9488' },
  { label: 'Biometric Devices Sync', color: '#4caf50' },
  { label: 'Corporate LMS Engine', color: '#f97316' },
  { label: 'Role-Based RBAC Security', color: '#1a2f6e' },
  { label: 'Multi-Warehouse Audits', color: '#0d9488' },
  { label: 'PDF Certificate Engine', color: '#f97316' },
  { label: '60% Less Manual Entry', color: '#4caf50' },
];

const CAPABILITIES = [
  {
    id: 'erp-systems',
    num: '01',
    icon: '📊',
    title: 'Enterprise ERP',
    fullTitle: 'Enterprise Resource Planning (ERP)',
    subtitle: 'Centralized purchasing, inventory & financial ledgers',
    desc: 'Unify inventory tracking, purchasing workflows, multi-currency financial ledger records, and supply chain logistics into one real-time operational dashboard.',
    deliverables: [
      'Real-Time Multi-Warehouse Inventory Audits',
      'Automated Purchase Order & Invoice Workflows',
      'Multi-Currency Financial Ledger Tracking',
      'Granular Role-Based Access Control (RBAC)',
    ],
    tags: ['Inventory Audits', 'Purchase Orders', 'Multi-Currency', 'Financial Ledgers'],
    accent: '#1a2f6e',
  },
  {
    id: 'hrm-payroll',
    num: '02',
    icon: '👥',
    title: 'HRM & Payroll',
    fullTitle: 'HRM & Automated Payroll Platforms',
    subtitle: 'Employee profiles, biometric attendance & payroll',
    desc: 'Manage employee onboarding, biometric attendance integration, automated salary slips, leave approval workflows, and performance appraisal KPIs.',
    deliverables: [
      'Automated Monthly Payroll Calculation',
      'Biometric Device Attendance Integration',
      'Leave Approval & Vacation Request Workflows',
      'Employee Performance Appraisal KPIs',
    ],
    tags: ['Automated Payroll', 'Biometric Sync', 'Leave Approvals', 'Performance KPIs'],
    accent: '#4caf50',
  },
  {
    id: 'crm-pipelines',
    num: '03',
    icon: '🤝',
    title: 'CRM Pipelines',
    fullTitle: 'CRM & Sales Pipeline Automation',
    subtitle: 'Lead tracking, deal pipelines & support ticketing',
    desc: 'Organize incoming sales leads, visual deal pipelines, customer interaction logs, automated email follow-ups, and support ticket queues.',
    deliverables: [
      'Visual Sales Funnel & Deal Stage Tracking',
      'Automated Email Drip Sequences & Follow-Ups',
      'Customer Interaction & History Logs',
      'Support Ticket Queue & SLA Escalations',
    ],
    tags: ['Visual Pipelines', 'Email Drips', 'Support Queue', 'Lead Scoring'],
    accent: '#0d9488',
  },
  {
    id: 'lms-training',
    num: '04',
    icon: '🎓',
    title: 'Corporate LMS',
    fullTitle: 'Corporate LMS & Training Platforms',
    subtitle: 'Interactive learning, quizzes & PDF certificates',
    desc: 'Deliver corporate training programs, course materials, streaming video lectures, student quizzes, and automated PDF completion certificates.',
    deliverables: [
      'Interactive Quiz & Assessment Engine',
      'Adaptive Video Player & Course Progress',
      'Student Analytics & Progress Dashboards',
      'Automated PDF Completion Certificate Engine',
    ],
    tags: ['Quiz Engine', 'Video Player', 'Analytics', 'PDF Certificates'],
    accent: '#f97316',
  },
];

const AnimatedText = ({ phrases }) => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % phrases.length);
        setVisible(true);
      }, 300);
    }, 2600);
    return () => clearInterval(timer);
  }, [phrases]);

  const current = phrases[index];

  return (
    <span
      style={{
        display: 'inline-block',
        color: current.color,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-6px)',
        transition: 'opacity 0.45s ease, transform 0.45s ease, color 0.45s ease',
        fontWeight: 700,
      }}
    >
      {current.text}
    </span>
  );
};

const BusinessSoftwarePage = () => {
  const [activeTabId, setActiveTabId] = useState('erp-systems');
  const [isChangingTab, setIsChangingTab] = useState(false);

  useEffect(() => {
    document.title = 'Business Software Solutions | Seikural Innovation';
    window.scrollTo(0, 0);
  }, []);

  useScrollReveal();

  const handleTabChange = (id) => {
    if (id === activeTabId) return;
    setIsChangingTab(true);
    setTimeout(() => {
      setActiveTabId(id);
      setIsChangingTab(false);
    }, 220);
  };

  const activeCapability = CAPABILITIES.find((c) => c.id === activeTabId) || CAPABILITIES[0];

  return (
    <div className="sipl-home-page-wrapper text-dark overflow-hidden">
      {/* Ambient background glow spots (Logo color palette) */}
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '1%', left: '-4%', width: 550, height: 550, background: 'radial-gradient(circle, rgba(76,175,80,0.045) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '30%', right: '-6%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(26,47,110,0.04) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '70%', left: '-5%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(13,148,136,0.035) 0%, transparent 70%)' }} />

      {/* ── 1. HERO SECTION ── */}
      <section
        className="border-bottom sipl-section-transparent sipl-hero"
        style={{
          position: 'relative',
          zIndex: 1,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 60%, #ffffff 100%)',
          minHeight: 'calc(100vh - 80px)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div className="container-fluid px-3 px-sm-4 px-lg-5 py-4" style={{ position: 'relative', zIndex: 1, maxWidth: '1680px' }}>
          <div className="row align-items-center g-4 g-xl-5">

            {/* Left Column Text */}
            <div className="col-12 col-lg-5">
              <span
                className="text-uppercase fw-bold d-block mb-2 sipl-reveal-ttb"
                style={{ color: '#0284c7', fontSize: '0.8rem', letterSpacing: '0.12em', '--gr-delay': '120ms' }}
              >
                BUSINESS SOFTWARE SOLUTIONS
              </span>

              <h1
                className="mb-3 sipl-reveal-ttb"
                style={{
                  fontSize: 'clamp(1.8rem, 2.85vw, 2.45rem)',
                  letterSpacing: '-0.025em',
                  lineHeight: 1.22,
                  color: '#0f172a',
                  fontWeight: 600,
                  '--gr-delay': '180ms',
                }}
              >
                <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>Business Software</span>
                <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>Solutions</span>
              </h1>

              <div className="mb-3" style={{ minHeight: 36 }}>
                <AnimatedText phrases={DYNAMIC_MESSAGES} />
              </div>

              <div
                className="mb-4 sipl-reveal-ttb"
                style={{ paddingLeft: '0', '--gr-delay': '320ms' }}
              >
                <p
                  className="mb-0"
                  style={{
                    fontSize: '0.96rem',
                    lineHeight: 1.68,
                    maxWidth: 540,
                    color: '#475569',
                  }}
                >
                  Replace spreadsheets with integrated ERP, CRM, HRM, and LMS modules tailored to your exact team workflows.
                </p>
              </div>

              <div
                className="d-flex flex-wrap align-items-center gap-3 mb-4 sipl-reveal-ttb"
                style={{ '--gr-delay': '460ms' }}
              >
                <CtaButton
                  to="/contact"
                  variant="primary"
                >
                  Request Software Demo
                </CtaButton>
                <CtaButton
                  href="#capabilities"
                  variant="secondary"
                  arrowDirection="down"
                >
                  View Modules
                </CtaButton>
              </div>

              <div
                className="sipl-reveal-ttb"
                style={{
                  fontSize: '0.84rem',
                  color: '#64748b',
                  fontWeight: 500,
                  letterSpacing: '-0.01em',
                  '--gr-delay': '600ms',
                }}
              >
                ERP Systems · CRM Platforms · HRM Tools · LMS Solutions
              </div>
            </div>

            {/* Right Column Image */}
            <div
              className="col-12 col-lg-7 d-flex align-items-center justify-content-center sipl-reveal-ttb"
              style={{ '--gr-delay': '350ms' }}
            >
              <div
                className="w-100 position-relative"
                style={{
                  height: 'clamp(260px, 38vw, 440px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#f8fafc',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: '0 16px 36px -10px rgba(15, 23, 42, 0.16), 0 4px 12px rgba(15, 23, 42, 0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {Images?.businessheader ? (
                  <img
                    src={Images.businessheader}
                    alt="Business Software ERP CRM Dashboard"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                  />
                ) : (
                  <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Image coming soon</span>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── 2. CONTINUOUS INFINITE SCROLL TICKER MARQUEE STRIP ── */}
      <div className="sipl-marquee-container bg-white border-top border-bottom py-2.5">
        <div className="sipl-marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, idx) => (
            <span
              key={idx}
              className="badge bg-light text-dark border px-3.5 py-2 rounded-pill font-weight-semibold mx-1"
              style={{ fontSize: '0.82rem', color: '#334155' }}
            >
              <span style={{ color: item.color, marginRight: 6 }}>●</span> {item.label}
            </span>
          ))}
        </div>
      </div>

      {/* ── 3. SEGMENTED TAB SELECTOR + ANIMATED DETAILS PANEL BELOW ── */}
      <section id="capabilities" className="py-5 bg-light">
        <div className="container py-4">
          {/* Section Header Centered */}
          <div className="text-center mb-4" style={{ maxWidth: 640, margin: '0 auto' }}>
            <span className="text-uppercase fw-bold small" style={{ color: '#1a2f6e', letterSpacing: '0.08em' }}>
              ENTERPRISE SUITE
            </span>
            <h2 className="h3 fw-bold text-dark mb-2">Core Software Systems</h2>
            <p className="text-muted small mb-0">Select any software module below to inspect features and integration specs.</p>
          </div>

          {/* SINGLE ROW SEGMENTED CONTROL WRAPPER */}
          <div className="mb-5 sipl-tabs-container" style={{ textAlign: 'center', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '4px' }}>
            <div
              role="tablist"
              style={{ display: 'inline-flex', flexWrap: 'nowrap', alignItems: 'center', gap: '6px', padding: '6px', backgroundColor: '#ffffff', borderRadius: '999px', boxShadow: '0 1px 4px rgba(15,23,42,0.08)', border: '1px solid #e2e8f0' }}
            >
              {CAPABILITIES.map((tab) => {
                const isActive = activeTabId === tab.id;
                return (
                  <button
                    type="button"
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`btn rounded-pill px-3 py-2 fw-bold text-nowrap d-inline-flex align-items-center gap-2`}
                    style={{
                      backgroundColor: isActive ? '#1a2f6e' : 'transparent',
                      color: isActive ? '#ffffff' : '#475569',
                      fontSize: '0.86rem',
                      border: 'none',
                      transform: isActive ? 'scale(1.03)' : 'scale(1)',
                      transition: 'all 0.3s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ fontSize: '0.95rem' }}>{tab.icon}</span>
                    <span>{tab.title}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ACTIVE CAPABILITY DETAILS PANEL WITH LONGER SMOOTH TRANSITION */}
          <div className="mx-auto" style={{ maxWidth: 940 }}>
            <div
              className="card border shadow-sm rounded-4 p-4 p-md-5 bg-white sipl-card-hover"
              style={{
                borderTop: `4px solid #1a2f6e`,
                minHeight: '380px',
                opacity: isChangingTab ? 0 : 1,
                transform: isChangingTab ? 'translateY(12px)' : 'translateY(0)',
                transition: 'opacity 0.35s ease, transform 0.35s ease',
              }}
            >
              {/* Header Bar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-3 border-bottom">
                <div className="d-flex align-items-center gap-3">
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold fs-4"
                    style={{
                      width: 50,
                      height: 50,
                      backgroundColor: `${activeCapability.accent}15`,
                      color: activeCapability.accent,
                      transition: 'all 0.4s ease',
                    }}
                  >
                    {activeCapability.icon}
                  </span>
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="badge px-2.5 py-1 rounded-pill fw-bold text-uppercase"
                        style={{ backgroundColor: `${activeCapability.accent}15`, color: activeCapability.accent, fontSize: '0.72rem' }}
                      >
                        {activeCapability.num} / ENTERPRISE MODULE
                      </span>
                    </div>
                    <h3 className="h4 fw-bold text-dark mb-0 mt-1">{activeCapability.fullTitle}</h3>
                    <div className="text-muted small">{activeCapability.subtitle}</div>
                  </div>
                </div>

                <Link
                  to="/contact"
                  className="btn px-4 py-2 rounded-3 fw-bold text-white shadow-sm sipl-arrow-link"
                  style={{ backgroundColor: '#1a2f6e', border: 'none', fontSize: '0.85rem', transition: 'all 0.3s ease' }}
                >
                  <span>Request Demo</span> <span className="ms-1">→</span>
                </Link>
              </div>

              {/* Description */}
              <p className="text-muted small mb-4" style={{ lineHeight: 1.7, fontSize: '0.94rem' }}>
                {activeCapability.desc}
              </p>

              {/* Deliverables 2x2 Grid with Interactive Hover Float */}
              <div className="mb-4">
                <div className="text-uppercase fw-bold mb-3" style={{ fontSize: '0.7rem', letterSpacing: '0.08em', color: activeCapability.accent }}>
                  Key Module Capabilities
                </div>
                <div className="row g-3">
                  {activeCapability.deliverables.map((del) => (
                    <div key={del} className="col-12 col-sm-6">
                      <div
                        className="d-flex align-items-center gap-3 p-3 rounded-3 bg-light border h-100 sipl-card-hover"
                        style={{ transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
                      >
                        <span
                          className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: `${activeCapability.accent}15`,
                            color: activeCapability.accent,
                            fontSize: '0.78rem',
                          }}
                        >
                          ✓
                        </span>
                        <span className="small text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{del}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tech Stack Strip */}
              <div className="pt-3.5 border-top d-flex flex-wrap align-items-center gap-2">
                <span className="text-uppercase fw-bold text-muted me-1" style={{ fontSize: '0.68rem', letterSpacing: '0.08em' }}>Scope:</span>
                {activeCapability.tags.map((tag) => (
                  <span key={tag} className="badge bg-light text-dark border px-3 py-1.5 rounded-pill fw-semibold" style={{ fontSize: '0.78rem' }}>
                    <span style={{ color: activeCapability.accent, marginRight: 5 }}>●</span> {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. MINIMAL CTA BANNER ── */}
      <section className="py-5 bg-white border-top sipl-reveal-btt">
        <div className="container text-center py-2">
          <div className="max-w-xl mx-auto" style={{ maxWidth: 560 }}>
            <h2 className="h4 fw-bold text-dark mb-2">Streamline Your Operations Today</h2>
            <p className="text-muted small mb-4">Empower your team with custom ERP, CRM, and HRM software.</p>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              <CtaButton to="/contact" variant="primary">
                Consult a Software Expert
              </CtaButton>
              <CtaButton to="/services/web-development" variant="secondary">
                Explore All Services
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessSoftwarePage;
